import React, { useEffect, useRef, useState, MutableRefObject } from 'react';
import { PitchSample, segmentNotes, toTab, tabText, letterOf, flatOf, octaveOf, STRING_NAMES, TabNote } from '../utils/transcribe';

interface Take { id: string; label: string; pitches: PitchSample[]; }

interface Props {
  /** Live pitch samples gathered while writing is on. */
  liveRef: MutableRefObject<PitchSample[]>;
  writing: boolean;
  onToggleWriting: () => void;
  onClear: () => void;
  takes: Take[];
  onWrote?: (notes: number) => void;
}

// Mess around and it writes the tab. Each note shows its letter, its octave
// and its Hz as three separate things, and where it sits on the neck.
export default function WritePanel({ liveRef, writing, onToggleWriting, onClear, takes, onWrote }: Props) {
  const [source, setSource] = useState<string>('live');
  const [tab, setTab] = useState<TabNote[]>([]);
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Live: rewrite a few times a second while playing. From a take: write once.
  useEffect(() => {
    if (source !== 'live') {
      const t = takes.find(x => x.id === source);
      setTab(t ? toTab(segmentNotes(t.pitches)) : []);
      return;
    }
    const tick = () => setTab(toTab(segmentNotes(liveRef.current)));
    tick();
    if (!writing) return;
    const id = window.setInterval(tick, 300);
    return () => clearInterval(id);
  }, [source, writing, takes, liveRef]);

  // keep the newest note in view
  useEffect(() => {
    const el = scrollRef.current;
    if (el && source === 'live') el.scrollLeft = el.scrollWidth;
  }, [tab.length, source]);

  const text = () => tabText(tab);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text()); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* clipboard refused */ }
    onWrote?.(tab.length);
  };
  const save = () => {
    const blob = new Blob([text() + '\n'], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `songbird-tab-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    onWrote?.(tab.length);
  };

  return (
    <div className="write-panel">
      <div className="write-head">
        <h4>Write</h4>
        <select value={source} onChange={e => setSource(e.target.value)} title="Write from what you are playing now, or from a take you recorded">
          <option value="live">from what I play now</option>
          {takes.map(t => <option key={t.id} value={t.id}>from take · {t.label}</option>)}
        </select>
        {source === 'live' && (
          <button className={`tbtn${writing ? ' recording' : ''}`} onClick={onToggleWriting}
            title={writing ? 'Stop writing; what you played stays' : 'Start writing: play or sing and the notes and tab appear'}>
            {writing ? '◼ Stop' : '✎ Write'}
          </button>
        )}
        {source === 'live' && <button className="tbtn" onClick={() => { onClear(); setTab([]); }} title="Start a fresh page">Clear</button>}
        <span className="write-count">{tab.length} note{tab.length === 1 ? '' : 's'}</span>
        <button className="tbtn" onClick={copy} disabled={!tab.length} title="Copy the tab as text">{copied ? 'Copied' : 'Copy'}</button>
        <button className="tbtn" onClick={save} disabled={!tab.length} title="Save the tab as a text file">Save</button>
      </div>

      <div className="write-body" ref={scrollRef}>
        <div className="write-strings">
          <div className="write-cell-lbl names" title="Letter">note</div>
          <div className="write-cell-lbl names" title="Octave">oct</div>
          <div className="write-cell-lbl names" title="Frequency">Hz</div>
          {[5, 4, 3, 2, 1, 0].map(s => <div key={s} className="write-cell-lbl">{STRING_NAMES[s]}</div>)}
        </div>
        {tab.length === 0 && (
          <div className="write-empty">
            {source === 'live'
              ? (writing ? 'Listening. Play or sing one note at a time.' : 'Press Write and mess around. The tab writes itself.')
              : 'No notes found in that take.'}
          </div>
        )}
        {tab.map((n, i) => {
          const sharp = letterOf(n.midi), flat = flatOf(n.midi);
          return (
            <div key={i} className="write-col" title={`${sharp}${sharp !== flat ? ' / ' + flat : ''}, octave ${octaveOf(n.midi)}, ${n.hz} Hz (${n.cents > 0 ? '+' : ''}${n.cents}¢ from the key), held ${(n.durMs / 1000).toFixed(2)} s${n.fret >= 0 ? `, string ${STRING_NAMES[n.string]} fret ${n.fret}` : ', off the guitar neck'}`}
              style={{ minWidth: `${Math.max(22, Math.min(64, n.durMs / 12))}px` }}>
              <div className="write-cell names letter">{sharp}</div>
              <div className="write-cell names">{octaveOf(n.midi)}</div>
              <div className="write-cell names hz">{Math.round(n.hz)}</div>
              {[5, 4, 3, 2, 1, 0].map(s => (
                <div key={s} className={`write-cell string${s === n.string ? ' hit' : ''}`}>{s === n.string ? n.fret : ''}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
